import { useEffect, useRef } from 'react';

import { useGFHLContext } from 'containers/GenericFieldsHandlingLayer/context';
import { track } from 'utils/kitAnalytics';
import { cloneSession } from './api';
import type { IPendingAssembly } from './types';

interface ISessionRotationBridgeProps {
  getPendingAssembly: (sessionId: string) => IPendingAssembly | null;
  isConfirmPageMounted: () => boolean;
  getWorkspaceKey: () => string | null;
}

/**
 * Keeps analytics one-session-per-assembly. Once the confirm-page assembly is
 * committed, its client session must stay frozen: the first wizard edit made
 * OUTSIDE the confirm page (the user went back via the step tabs) clones the
 * session and moves the wizard onto the clone, so the edit lands there and the
 * next commit becomes a separate assembly on its own session. Edits on the
 * confirm page itself refine the committed assembly and do not rotate.
 *
 * Rendered by KitWorkspaceStorage for every tenant; without kit commits the
 * registry is empty and the interceptor is a no-op. Clone failures fall back
 * to keeping the current session (pre-fix behavior).
 */
const SessionRotationBridge = ({
  getPendingAssembly, isConfirmPageMounted, getWorkspaceKey,
}: ISessionRotationBridgeProps) => {
  const { session, actions } = useGFHLContext();
  const { registerBeforeFieldsUpdate } = actions;

  const sessionIdRef = useRef(session.sessionId);
  sessionIdRef.current = session.sessionId;
  // Rapid edits share one clone request instead of racing several.
  const cloneInFlightRef = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    registerBeforeFieldsUpdate(() => {
      const sessionId = sessionIdRef.current;
      if (!sessionId || isConfirmPageMounted() || !getPendingAssembly(sessionId)) {
        return Promise.resolve(null);
      }
      if (!cloneInFlightRef.current) {
        cloneInFlightRef.current = cloneSession(sessionId)
          .then(({ sessionId: rotatedId }) => {
            track('session_reset', {
              workspaceKey: getWorkspaceKey() ?? undefined, reason: 'edit_after_save', from: sessionId,
            });
            return rotatedId;
          })
          .catch(() => null)
          .finally(() => { cloneInFlightRef.current = null; });
      }
      return cloneInFlightRef.current;
    });
    return () => registerBeforeFieldsUpdate(null);
  }, [registerBeforeFieldsUpdate, getPendingAssembly, isConfirmPageMounted, getWorkspaceKey]);

  return null;
};

export default SessionRotationBridge;
